<template>
  <ToolDropdown :data-testid="`block-format-dropdown`" :options="options" tooltip="Block format" />
</template>

<script lang="ts" setup>
import { Editor } from "@tiptap/core";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import ToolDropdown from "../_internal/ToolDropdown.vue";

const props = defineProps<{
  editor: Editor;
  language?: string;
}>();

const { t } = useI18n({
  locale: props.language || "en",
  inheritLocale: true,
  fallbackLocale: "en",
  useScope: "local",
});

const options = computed(() => {
  return [
    {
      format: "p",
      text: t("settings_paragraph"),
      set: () => props.editor.chain().focus().setParagraph().run(),
      isActive: props.editor.isActive("paragraph"),
    },
    {
      format: "h1",
      text: t("settings_heading1"),
      set: () => props.editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: props.editor.isActive("heading", { level: 1 }),
    },
    {
      format: "h2",
      text: t("settings_heading2"),
      set: () => props.editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: props.editor.isActive("heading", { level: 2 }),
    },
    {
      format: "h3",
      text: t("settings_heading3"),
      set: () => props.editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: props.editor.isActive("heading", { level: 3 }),
    },
  ];
});
</script>

<i18n lang="json">
{
  "ar": {
    "settings_heading1": "عنوان 1",
    "settings_heading2": "العنوان 2",
    "settings_heading3": "العنوان 3",
    "settings_paragraph": "فقرة"
  },
  "cs": {
    "settings_heading1": "Nadpis H1",
    "settings_heading2": "Nadpis H2",
    "settings_heading3": "Nadpis H3",
    "settings_paragraph": "Běžný text"
  },
  "de": {
    "settings_heading1": "Überschrift 1",
    "settings_heading2": "Überschrift 2",
    "settings_heading3": "Überschrift 3",
    "settings_paragraph": "Absatz"
  },
  "en": {
    "settings_heading1": "Heading 1",
    "settings_heading2": "Heading 2",
    "settings_heading3": "Heading 3",
    "settings_paragraph": "Paragraph"
  },
  "es": {
    "settings_heading1": "Título 1",
    "settings_heading2": "Título 2",
    "settings_heading3": "Título 3",
    "settings_paragraph": "Párrafo"
  },
  "fi": {
    "settings_heading1": "Otsikko 1",
    "settings_heading2": "Otsikko 2",
    "settings_heading3": "Otsikko 3",
    "settings_paragraph": "Kohta"
  },
  "fr": {
    "settings_heading1": "Titre 1",
    "settings_heading2": "Titre 2",
    "settings_heading3": "Titre 3",
    "settings_paragraph": "Paragraphe"
  },
  "he": {
    "settings_heading1": "כותרת 1",
    "settings_heading2": "כותרת 2",
    "settings_heading3": "כותרת 3",
    "settings_paragraph": "פסקה"
  },
  "it": {
    "settings_heading1": "Intestazione 1",
    "settings_heading2": "Intestazione 2",
    "settings_heading3": "Intestazione 3",
    "settings_paragraph": "Paragrafo"
  },
  "ja": {
    "settings_heading1": "見出し1",
    "settings_heading2": "見出し2",
    "settings_heading3": "見出し3",
    "settings_paragraph": "段落"
  },
  "ko": {
    "settings_heading1": "제목 1",
    "settings_heading2": "제목 2",
    "settings_heading3": "제목 3",
    "settings_paragraph": "문단"
  },
  "nl": {
    "settings_heading1": "Kop 1",
    "settings_heading2": "Kop 2",
    "settings_heading3": "Kop 3",
    "settings_paragraph": "Paragraaf"
  },
  "pl": {
    "settings_heading1": "Nagłówek 1",
    "settings_heading2": "Nagłówek 2",
    "settings_heading3": "Nagłówek 3",
    "settings_paragraph": "Tekst zwykły"
  },
  "pt": {
    "settings_heading1": "Cabeçallho 1",
    "settings_heading2": "Cabeçalho 2",
    "settings_heading3": "Cabeçalho 3",
    "settings_paragraph": "Parágrafo"
  },
  "ro": {
    "settings_heading1": "Rubrica 1",
    "settings_heading2": "Rubrica 2",
    "settings_heading3": "Rubrica 3",
    "settings_paragraph": "Paragraf"
  },
  "ru": {
    "settings_heading1": "Заголовок 1",
    "settings_heading2": "Заголовок 2",
    "settings_heading3": "Заголовок 3",
    "settings_paragraph": "Параграфы"
  },
  "sk": {
    "settings_heading1": "Nadpis 1",
    "settings_heading2": "Nadpis 2",
    "settings_heading3": "Nadpis 3",
    "settings_paragraph": "Odstavec"
  },
  "sv": {
    "settings_heading1": "Rubrik 1",
    "settings_heading2": "Rubrik 2",
    "settings_heading3": "Rubrik 3",
    "settings_paragraph": "Paragraf"
  },
  "tr": {
    "settings_heading1": "Başlık 1",
    "settings_heading2": "Başlık 2",
    "settings_heading3": "Başlık 3",
    "settings_paragraph": "Paragraf"
  },
  "vi": {
    "settings_heading1": "Tiêu đề 1",
    "settings_heading2": "Tiêu đề 2",
    "settings_heading3": "Tiêu đề 3",
    "settings_paragraph": "Đoạn"
  },
  "zh": {
    "settings_heading1": "标题1",
    "settings_heading2": "标题2",
    "settings_heading3": "标题3",
    "settings_paragraph": "段"
  }
}
</i18n>
